import { useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import AutocompleteField from "elements/AutocompleteField";
import { LineItem, generateId } from "../helpers/invoiceUtils";

interface Props {
  open: boolean;
  onClose: () => void;
  onAdd: (item: LineItem) => void;
}

export default function AddItemDialog({ open, onClose, onAdd }: Props) {
  const [mode, setMode] = useState<"search" | "free">("search");
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [form, setForm] = useState({ product_code: "", description: "", quantity: 1, price: 0, discount_percent: 0, tax_percent: 19 });

  const reset = () => {
    setMode("search");
    setSelectedProduct(null);
    setForm({ product_code: "", description: "", quantity: 1, price: 0, discount_percent: 0, tax_percent: 19 });
  };

  const handleProductSelect = (product: any) => {
    setSelectedProduct(product);
    if (product) {
      setForm({
        product_code: product.product_code || "",
        description: product.product_name || "",
        quantity: 1,
        price: product.price_1 || 0,
        discount_percent: 0,
        tax_percent: product.tax_1 || 19,
      });
    }
  };

  const handleAdd = () => {
    if (!form.description.trim()) return;
    onAdd({
      id: generateId(),
      product_code: form.product_code,
      description: form.description,
      quantity: form.quantity,
      price: form.price,
      discount_percent: form.discount_percent,
      tax_percent: form.tax_percent,
      unit_code: "NIU",
    });
    reset();
    onClose();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Agregar Ítem</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
        <ToggleButtonGroup value={mode} exclusive onChange={(_, v) => v && setMode(v)} size="small" fullWidth>
          <ToggleButton value="search">Buscar Producto</ToggleButton>
          <ToggleButton value="free">Producto Libre</ToggleButton>
        </ToggleButtonGroup>

        {mode === "search" ? (
          <>
            <AutocompleteField
              label="Buscar por nombre o código"
              endpoint="/products/list"
              renderValues={["product_code", "label"]}
              value={selectedProduct?.product_id || null}
              onChange={handleProductSelect}
              fullwidth
            />
            {selectedProduct && (
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <TextField label="Cantidad" type="number" size="small" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) })} inputProps={{ min: 1 }} sx={{ width: 100 }} />
                <TextField label="Precio" type="number" size="small" value={form.price} onChange={(e) => setForm({ ...form, price: Number(e.target.value) })} inputProps={{ min: 0 }} sx={{ width: 130 }} />
                <TextField label="Dto.%" type="number" size="small" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: Number(e.target.value) })} inputProps={{ min: 0, max: 100 }} sx={{ width: 90 }} />
                <TextField label="IVA" select size="small" value={form.tax_percent} onChange={(e) => setForm({ ...form, tax_percent: Number(e.target.value) })} sx={{ width: 90 }}>
                  <MenuItem value={0}>0%</MenuItem>
                  <MenuItem value={5}>5%</MenuItem>
                  <MenuItem value={19}>19%</MenuItem>
                </TextField>
              </div>
            )}
          </>
        ) : (
          <>
            <TextField label="Código" size="small" value={form.product_code} onChange={(e) => setForm({ ...form, product_code: e.target.value })} />
            <TextField label="Descripción" size="small" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <TextField label="Cantidad" type="number" size="small" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) })} inputProps={{ min: 1 }} sx={{ width: 100 }} />
              <TextField label="Precio" type="number" size="small" value={form.price} onChange={(e) => setForm({ ...form, price: Number(e.target.value) })} inputProps={{ min: 0 }} sx={{ width: 130 }} />
              <TextField label="Dto.%" type="number" size="small" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: Number(e.target.value) })} inputProps={{ min: 0, max: 100 }} sx={{ width: 90 }} />
              <TextField label="IVA" select size="small" value={form.tax_percent} onChange={(e) => setForm({ ...form, tax_percent: Number(e.target.value) })} sx={{ width: 90 }}>
                <MenuItem value={0}>0%</MenuItem>
                <MenuItem value={5}>5%</MenuItem>
                <MenuItem value={19}>19%</MenuItem>
              </TextField>
            </div>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleAdd} disabled={!form.description.trim() || form.price <= 0 || form.quantity <= 0}>
          Agregar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
